class ProductThumbnailNavigation extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.scroller = this.querySelector('.product__thumbnails-list');
    this.previousButton = this.querySelector('[data-thumbnail-previous]');
    this.nextButton = this.querySelector('[data-thumbnail-next]');
    if (!this.scroller || !this.previousButton || !this.nextButton) return;

    this.initialized = true;
    this.previousClickHandler = () => this.scrollByPage(-1);
    this.nextClickHandler = () => this.scrollByPage(1);
    this.scrollHandler = () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateControls();
      });
    };

    this.previousButton.addEventListener('click', this.previousClickHandler);
    this.nextButton.addEventListener('click', this.nextClickHandler);
    this.scroller.addEventListener('scroll', this.scrollHandler, { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.scroller);
    requestAnimationFrame(() => this.updateControls());
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    this.previousButton.removeEventListener('click', this.previousClickHandler);
    this.nextButton.removeEventListener('click', this.nextClickHandler);
    this.scroller.removeEventListener('scroll', this.scrollHandler);
    this.resizeObserver?.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    if (this.scrollAnimationFrame) cancelAnimationFrame(this.scrollAnimationFrame);
    this.initialized = false;
  }

  scrollByPage(direction) {
    const firstThumbnail = Array.from(this.scroller.querySelectorAll('.product__thumbnail')).find((thumbnail) => thumbnail.clientWidth > 0);
    const styles = window.getComputedStyle(this.scroller);
    const gap = parseFloat(styles.columnGap) || 0;
    const step = (firstThumbnail?.offsetWidth || 68) + gap;
    const visibleCount = Math.max(1, Math.min(4, Math.floor(this.scroller.clientWidth / step) - 1));
    const maxScroll = Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
    const currentScroll = Math.abs(this.scroller.scrollLeft);
    const targetScroll = Math.min(maxScroll, Math.max(0, currentScroll + direction * step * visibleCount));
    const directionMultiplier = document.documentElement.dir === 'rtl' ? -1 : 1;

    const targetLeft = directionMultiplier * targetScroll;
    this.scrollToTarget(targetLeft);

    this.previousButton.disabled = targetScroll <= 2;
    this.nextButton.disabled = targetScroll >= maxScroll - 2;
  }

  scrollToTarget(targetLeft) {
    if (this.scrollAnimationFrame) cancelAnimationFrame(this.scrollAnimationFrame);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.scroller.scrollLeft = targetLeft;
      this.updateControls();
      return;
    }

    const startLeft = this.scroller.scrollLeft;
    const distance = targetLeft - startLeft;
    const startTime = performance.now();
    const duration = 440;

    const animate = (time) => {
      const progress = Math.min(1, (time - startTime) / duration);
      const easedProgress = progress < 0.5
        ? 4 * Math.pow(progress, 3)
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      this.scroller.scrollLeft = startLeft + distance * easedProgress;

      if (progress < 1) {
        this.scrollAnimationFrame = requestAnimationFrame(animate);
      } else {
        this.scrollAnimationFrame = null;
        this.updateControls();
      }
    };

    this.scrollAnimationFrame = requestAnimationFrame(animate);
  }

  updateControls() {
    const maxScroll = Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
    const currentScroll = Math.abs(this.scroller.scrollLeft);
    const isScrollable = maxScroll > 2;
    const isAtStart = !isScrollable || currentScroll <= 2;
    const isAtEnd = !isScrollable || currentScroll >= maxScroll - 2;

    this.toggleAttribute('data-scrollable', isScrollable);
    this.toggleAttribute('data-at-start', isScrollable && isAtStart);
    this.toggleAttribute('data-at-end', isScrollable && isAtEnd);
    this.previousButton.disabled = isAtStart;
    this.nextButton.disabled = isAtEnd;
  }
}

if (!customElements.get('product-thumbnail-navigation')) {
  customElements.define('product-thumbnail-navigation', ProductThumbnailNavigation);
}
